import React, { useMemo, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { IconPlus, IconPencil, IconTrash, IconListDetails } from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useLoading } from '../context/LoadingContext';
import { todayDateStringInTz, DAY_ANCHOR_TZ } from '../utils/time';
import { formatHolidayDate, sortHolidaysByDate, holidaysForList } from '../utils/holidays';
import ConfirmModal from '../components/ConfirmModal';
import SelectField from '../components/SelectField';

const WRITES_TO_SHEET = Boolean(process.env.REACT_APP_APPS_SCRIPT_URL);

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CALENDAR_CHIP_LIMIT = 3;

const EMPTY_HOLIDAY_FORM = { name: '', anchorDate: '' };
const EMPTY_LIST_FORM = { name: '', holidayIds: [] };

// Same inline re-auth pattern as Leaves/Announcements — a quick Google
// sign-in right in the modal footer when the token's gone stale.
function ReauthFooter({ setIdToken, onCancel }) {
  const { showError } = useToast();
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm text-muted text-center">Your session needs a quick refresh before this can be saved.</p>
      <GoogleLogin
        onSuccess={(credentialResponse) => setIdToken(credentialResponse.credential)}
        onError={() => showError('Google sign-in failed — please try again.')}
      />
      <button onClick={onCancel} className="text-sm font-medium text-muted hover:text-ink transition-colors">
        Cancel
      </button>
    </div>
  );
}

// Same month-grid pattern as Leaves' CalendarGrid — a holiday recurs on its
// month/day every year, so it shows up on that date in the grid regardless
// of which year is currently being viewed.
function HolidayCalendar({ holidays, holidayLists, holidayListItems, todayCst }) {
  const [monthDate, setMonthDate] = useState(() => `${todayCst.slice(0, 7)}-01`);
  const [filterListId, setFilterListId] = useState('');

  const year = Number(monthDate.slice(0, 4));
  const monthIndex = Number(monthDate.slice(5, 7)) - 1;
  const monthLabel = new Date(Date.UTC(year, monthIndex, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  const shiftMonth = (delta) => {
    const d = new Date(Date.UTC(year, monthIndex + delta, 1));
    setMonthDate(`${d.toISOString().slice(0, 7)}-01`);
  };

  const firstOfMonth = new Date(Date.UTC(year, monthIndex, 1));
  const firstWeekday = firstOfMonth.getUTCDay(); // 0=Sun..6=Sat
  const leadingDays = (firstWeekday + 6) % 7; // back up to the Monday on/before the 1st
  const gridStart = new Date(Date.UTC(year, monthIndex, 1 - leadingDays));

  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setUTCDate(gridStart.getUTCDate() + i);
    const dow = d.getUTCDay();
    return {
      dateStr: d.toISOString().slice(0, 10),
      day: d.getUTCDate(),
      month: d.getUTCMonth() + 1,
      inMonth: d.getUTCMonth() === monthIndex,
      isWeekend: dow === 0 || dow === 6,
    };
  });

  const visibleHolidays = useMemo(() => {
    if (!filterListId) return holidays;
    const idsInList = new Set(
      holidayListItems.filter((item) => item.list_id === filterListId).map((item) => item.holiday_id)
    );
    return holidays.filter((h) => idsInList.has(h.holiday_id));
  }, [holidays, holidayListItems, filterListId]);

  const holidaysOnDay = (cell) =>
    visibleHolidays.filter((h) => Number(h.month) === cell.month && Number(h.day) === cell.day);

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm mt-4 overflow-hidden">
      <div className="px-4 sm:px-6 py-3 border-b border-border">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink">{monthLabel}</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => shiftMonth(-1)}
              className="px-2 py-1 rounded-lg border border-border text-xs font-medium text-ink hover:bg-card-tint transition-colors"
            >
              ‹ Prev
            </button>
            <button
              onClick={() => shiftMonth(1)}
              className="px-2 py-1 rounded-lg border border-border text-xs font-medium text-ink hover:bg-card-tint transition-colors"
            >
              Next ›
            </button>
          </div>
        </div>
        <div className="mt-2">
          <SelectField
            value={filterListId}
            onChange={(e) => setFilterListId(e.target.value)}
            className="text-xs py-1"
          >
            <option value="">All holidays</option>
            {holidayLists.map((l) => (
              <option key={l.list_id} value={l.list_id}>
                {l.name}
              </option>
            ))}
          </SelectField>
        </div>
      </div>

      <div className="grid grid-cols-7">
        {WEEKDAY_LABELS.map((w) => (
          <div
            key={w}
            className="border-b border-border px-1 py-1.5 text-center text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-muted"
          >
            {w}
          </div>
        ))}
        {cells.map((cell) => {
          const hits = holidaysOnDay(cell);
          const visible = hits.slice(0, CALENDAR_CHIP_LIMIT);
          const overflow = hits.length - visible.length;
          return (
            <div
              key={cell.dateStr}
              className={`border-b border-r border-border p-1 sm:p-1.5 min-h-[4.25rem] sm:min-h-[6rem] [&:nth-child(7n+1)]:border-l ${
                !cell.inMonth ? 'bg-card-tint/50' : cell.isWeekend ? 'bg-light-gold/40' : ''
              }`}
            >
              <p className={`text-[10px] sm:text-xs font-medium ${cell.inMonth ? 'text-ink' : 'text-muted/60'}`}>
                {cell.day}
              </p>
              <div className="mt-0.5 flex flex-col gap-0.5">
                {visible.map((h) => (
                  <span
                    key={h.holiday_id}
                    title={h.name}
                    className="block truncate rounded px-1 py-0.5 text-[8px] sm:text-[10px] font-medium leading-tight bg-light-gold text-bronze"
                  >
                    {h.name}
                  </span>
                ))}
                {overflow > 0 && <span className="text-[8px] sm:text-[9px] text-muted">+{overflow} more</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Holidays() {
  const {
    holidays,
    holidayLists,
    holidayListItems,
    createHoliday,
    updateHoliday,
    deleteHoliday,
    createHolidayList,
    updateHolidayList,
    deleteHolidayList,
    setIdToken,
    isIdTokenValid,
  } = useApp();
  const { showSuccess, showError } = useToast();
  const { withLoading } = useLoading();
  const needsReauth = WRITES_TO_SHEET && !isIdTokenValid;

  const todayCst = useMemo(() => todayDateStringInTz(DAY_ANCHOR_TZ), []);
  const sortedHolidays = useMemo(() => sortHolidaysByDate(holidays), [holidays]);

  // --- Holiday create/edit/delete ---
  const [holidayFormOpen, setHolidayFormOpen] = useState(false);
  const [editingHolidayId, setEditingHolidayId] = useState(null);
  const [holidayForm, setHolidayForm] = useState(EMPTY_HOLIDAY_FORM);
  const [deleteHolidayTarget, setDeleteHolidayTarget] = useState(null);

  const openCreateHoliday = () => {
    setEditingHolidayId(null);
    setHolidayForm(EMPTY_HOLIDAY_FORM);
    setHolidayFormOpen(true);
  };

  const openEditHoliday = (h) => {
    setEditingHolidayId(h.holiday_id);
    setHolidayForm({
      name: h.name,
      anchorDate: `2001-${String(h.month).padStart(2, '0')}-${String(h.day).padStart(2, '0')}`,
    });
    setHolidayFormOpen(true);
  };

  const closeHolidayForm = () => setHolidayFormOpen(false);

  const confirmSaveHoliday = async () => {
    if (!holidayForm.name.trim()) {
      showError('Give the holiday a name.');
      return;
    }
    if (!holidayForm.anchorDate) {
      showError('Pick a date.');
      return;
    }
    const [, m, d] = holidayForm.anchorDate.split('-');
    const name = holidayForm.name.trim();
    const month = Number(m);
    const day = Number(d);

    try {
      if (editingHolidayId) {
        await withLoading(() => updateHoliday({ holidayId: editingHolidayId, name, month, day }));
        showSuccess('Holiday updated.');
      } else {
        await withLoading(() => createHoliday({ name, month, day }));
        showSuccess('Holiday added.');
      }
      closeHolidayForm();
    } catch (err) {
      showError(err.message || "Couldn't save that — please try again.");
    }
  };

  const confirmDeleteHoliday = async () => {
    if (!deleteHolidayTarget) return;
    try {
      await withLoading(() => deleteHoliday({ holidayId: deleteHolidayTarget }));
      showSuccess('Holiday deleted.');
      setDeleteHolidayTarget(null);
    } catch (err) {
      showError(err.message || "Couldn't delete that — please try again.");
    }
  };

  // --- Holiday List create/edit/delete ---
  const [listFormOpen, setListFormOpen] = useState(false);
  const [editingListId, setEditingListId] = useState(null);
  const [listForm, setListForm] = useState(EMPTY_LIST_FORM);
  const [deleteListTarget, setDeleteListTarget] = useState(null);
  const [holidaySearch, setHolidaySearch] = useState('');

  const openCreateList = () => {
    setEditingListId(null);
    setListForm(EMPTY_LIST_FORM);
    setHolidaySearch('');
    setListFormOpen(true);
  };

  const openEditList = (l) => {
    const memberIds = holidayListItems.filter((item) => item.list_id === l.list_id).map((item) => item.holiday_id);
    setEditingListId(l.list_id);
    setListForm({ name: l.name, holidayIds: memberIds });
    setHolidaySearch('');
    setListFormOpen(true);
  };

  const closeListForm = () => setListFormOpen(false);

  // Both of these only touch local form state — nothing is written to the
  // sheet until Save is clicked (confirmSaveList), same as every other field
  // in this modal.
  const addListHoliday = (holidayId) => {
    setListForm((f) => (f.holidayIds.includes(holidayId) ? f : { ...f, holidayIds: [...f.holidayIds, holidayId] }));
  };

  const removeListHoliday = (holidayId) => {
    setListForm((f) => ({ ...f, holidayIds: f.holidayIds.filter((id) => id !== holidayId) }));
  };

  // Holidays not yet in the list, filtered by the search box — what the
  // "add" panel shows.
  const availableHolidays = useMemo(() => {
    const query = holidaySearch.trim().toLowerCase();
    return sortedHolidays.filter(
      (h) => !listForm.holidayIds.includes(h.holiday_id) && (!query || h.name.toLowerCase().includes(query))
    );
  }, [sortedHolidays, listForm.holidayIds, holidaySearch]);

  // Holidays currently in the list, in calendar order — what the "in this
  // list" panel shows, each with its own Remove button.
  const addedHolidays = useMemo(
    () => sortHolidaysByDate(holidays.filter((h) => listForm.holidayIds.includes(h.holiday_id))),
    [holidays, listForm.holidayIds]
  );

  const confirmSaveList = async () => {
    if (!listForm.name.trim()) {
      showError('Give the list a name.');
      return;
    }
    const name = listForm.name.trim();
    try {
      if (editingListId) {
        await withLoading(() => updateHolidayList({ listId: editingListId, name, holidayIds: listForm.holidayIds }));
        showSuccess('Holiday list updated.');
      } else {
        await withLoading(() => createHolidayList({ name, holidayIds: listForm.holidayIds }));
        showSuccess('Holiday list created.');
      }
      closeListForm();
    } catch (err) {
      showError(err.message || "Couldn't save that — please try again.");
    }
  };

  const confirmDeleteList = async () => {
    if (!deleteListTarget) return;
    try {
      await withLoading(() => deleteHolidayList({ listId: deleteListTarget }));
      showSuccess('Holiday list deleted.');
      setDeleteListTarget(null);
    } catch (err) {
      showError(err.message || "Couldn't delete that — please try again.");
    }
  };

  return (
    <>
      <div className="max-w-3xl mx-auto">
        <p className="text-sm text-muted">
          Manage named holidays and group them into lists — lists will attach to team members later.
        </p>

        {/* Holidays section */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-ink uppercase tracking-wide">Holidays</h2>
          <button
            onClick={openCreateHoliday}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-white bg-gold hover:bg-bronze transition-colors text-xs whitespace-nowrap"
          >
            <IconPlus size={14} stroke={2} />
            New
          </button>
        </div>
        <div className="mt-3 space-y-2">
          {sortedHolidays.length === 0 && <p className="text-sm text-muted py-4">No holidays added yet.</p>}
          {sortedHolidays.map((h) => (
            <div
              key={h.holiday_id}
              className="bg-card border border-border rounded-xl p-3 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink truncate">{h.name}</p>
                <p className="text-xs text-muted">{formatHolidayDate(h)}</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1">
                <button
                  onClick={() => openEditHoliday(h)}
                  aria-label="Edit"
                  className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors"
                >
                  <IconPencil size={16} stroke={1.8} />
                </button>
                <button
                  onClick={() => setDeleteHolidayTarget(h.holiday_id)}
                  aria-label="Delete"
                  className="p-1.5 rounded-lg text-muted hover:text-error-tx hover:bg-card-tint transition-colors"
                >
                  <IconTrash size={16} stroke={1.8} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Holiday Lists section */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-ink uppercase tracking-wide">Holiday Lists</h2>
          <button
            onClick={openCreateList}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-white bg-gold hover:bg-bronze transition-colors text-xs whitespace-nowrap"
          >
            <IconPlus size={14} stroke={2} />
            New
          </button>
        </div>
        <div className="mt-3 space-y-2">
          {holidayLists.length === 0 && <p className="text-sm text-muted py-4">No holiday lists yet.</p>}
          {holidayLists.map((l) => {
            const members = holidaysForList(l.list_id, holidays, holidayListItems);
            return (
              <div key={l.list_id} className="bg-card border border-border rounded-xl p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink flex items-center gap-1.5">
                      <IconListDetails size={15} stroke={1.8} className="text-bronze flex-shrink-0" />
                      <span className="truncate">{l.name}</span>
                    </p>
                    <p className="text-xs text-muted mt-1">
                      {members.length === 0
                        ? 'No holidays in this list'
                        : members.map((h) => h.name).join(', ')}
                    </p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1">
                    <button
                      onClick={() => openEditList(l)}
                      aria-label="Edit"
                      className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors"
                    >
                      <IconPencil size={16} stroke={1.8} />
                    </button>
                    <button
                      onClick={() => setDeleteListTarget(l.list_id)}
                      aria-label="Delete"
                      className="p-1.5 rounded-lg text-muted hover:text-error-tx hover:bg-card-tint transition-colors"
                    >
                      <IconTrash size={16} stroke={1.8} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Calendar section */}
        <div className="mt-8">
          <h2 className="text-sm font-semibold text-ink uppercase tracking-wide">Calendar</h2>
          <HolidayCalendar
            holidays={holidays}
            holidayLists={holidayLists}
            holidayListItems={holidayListItems}
            todayCst={todayCst}
          />
        </div>
      </div>

      <ConfirmModal
        open={holidayFormOpen}
        title={editingHolidayId ? 'Edit holiday' : 'New holiday'}
        confirmLabel="Save"
        onConfirm={confirmSaveHoliday}
        onCancel={closeHolidayForm}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={closeHolidayForm} /> : undefined}
      >
        <div>
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Name</label>
          <input
            type="text"
            value={holidayForm.name}
            onChange={(e) => setHolidayForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Christmas Day"
            className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
          />
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Date</label>
          <input
            type="date"
            value={holidayForm.anchorDate}
            onChange={(e) => setHolidayForm((f) => ({ ...f, anchorDate: e.target.value }))}
            className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
          />
          <p className="text-[11px] text-muted mt-1">Only the month/day is used — it repeats every year.</p>
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(deleteHolidayTarget)}
        title="Delete this holiday?"
        confirmLabel="Delete"
        onConfirm={confirmDeleteHoliday}
        onCancel={() => setDeleteHolidayTarget(null)}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={() => setDeleteHolidayTarget(null)} /> : undefined}
      >
        <p>This also removes it from any holiday list it's part of. This can't be undone.</p>
      </ConfirmModal>

      <ConfirmModal
        open={listFormOpen}
        title={editingListId ? 'Edit holiday list' : 'New holiday list'}
        confirmLabel="Save"
        onConfirm={confirmSaveList}
        onCancel={closeListForm}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={closeListForm} /> : undefined}
      >
        <div>
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Name</label>
          <input
            type="text"
            value={listForm.name}
            onChange={(e) => setListForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. PH Holidays 2026"
            className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
          />
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Add holidays</label>
          {sortedHolidays.length === 0 ? (
            <p className="text-sm text-muted">Add a holiday first before building a list.</p>
          ) : (
            <>
              <input
                type="text"
                value={holidaySearch}
                onChange={(e) => setHolidaySearch(e.target.value)}
                placeholder="Search holidays..."
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <div className="mt-2 max-h-32 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {availableHolidays.length === 0 ? (
                  <p className="text-xs text-muted px-3 py-2">
                    {holidaySearch.trim() ? 'No matches.' : 'Every holiday is already in this list.'}
                  </p>
                ) : (
                  availableHolidays.map((h) => (
                    <div key={h.holiday_id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="flex-1 text-ink truncate">{h.name}</span>
                      <span className="text-xs text-muted whitespace-nowrap">{formatHolidayDate(h)}</span>
                      <button
                        type="button"
                        onClick={() => addListHoliday(h.holiday_id)}
                        className="text-xs font-semibold text-bronze hover:text-ink transition-colors flex-shrink-0"
                      >
                        Add
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {addedHolidays.length > 0 && (
          <div className="mt-3">
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">In this list</label>
            <div className="border border-border rounded-lg divide-y divide-border">
              {addedHolidays.map((h) => (
                <div key={h.holiday_id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <span className="flex-1 text-ink truncate">{h.name}</span>
                  <span className="text-xs text-muted whitespace-nowrap">{formatHolidayDate(h)}</span>
                  <button
                    type="button"
                    onClick={() => removeListHoliday(h.holiday_id)}
                    className="text-xs font-semibold text-error-tx hover:text-ink transition-colors flex-shrink-0"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(deleteListTarget)}
        title="Delete this holiday list?"
        confirmLabel="Delete"
        onConfirm={confirmDeleteList}
        onCancel={() => setDeleteListTarget(null)}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={() => setDeleteListTarget(null)} /> : undefined}
      >
        <p>The holidays themselves aren't deleted, only this grouping. This can't be undone.</p>
      </ConfirmModal>
    </>
  );
}
